/** Wizard steps in order. */
export type WizardStepKey =
  | 'customer'
  | 'address'
  | 'constructionSite'
  | 'deliveryType';

/** Steps offered for a customer. A Barverkauf account is the collector for
    walk-in customers: it never has Baustellen, so that step is dropped, and
    the walk-in's own address has to be captured on the document instead. */
export function wizardStepKeys(
  customer: { oneTimeCustomer?: boolean } | null | undefined
): WizardStepKey[] {
  return customer?.oneTimeCustomer
    ? ['customer', 'address', 'deliveryType']
    : ['customer', 'constructionSite', 'deliveryType'];
}

/** Fields the Barverkauf address cannot be created without — same set the
    document form marks required (`customerAddressRequired`). */
const REQUIRED_ADDRESS_FIELDS = ['name1', 'street', 'postCode', 'city'] as const;

export function isCustomerAddressComplete(
  address: Partial<Record<(typeof REQUIRED_ADDRESS_FIELDS)[number], string | null>> | null
): boolean {
  if (!address) return false;
  return REQUIRED_ADDRESS_FIELDS.every((field) => !!address[field]?.trim());
}

/** Step rendered at `index`; falls back to the first step when the index no
    longer exists because the step list shrank. */
export function wizardStepAt(
  keys: readonly WizardStepKey[],
  index: number
): WizardStepKey {
  return keys[index] ?? 'customer';
}

export function isLastWizardStep(keys: readonly WizardStepKey[], index: number): boolean {
  return index >= keys.length - 1;
}

/** Kundennummer/Subnummer of a Baustelle (Sub-Kunde) row for display labels. */
export function constructionSiteNumberLabel(
  site: { customerNumber?: number | null; customerSubnumber?: number | null } | null
): string | null {
  if (site?.customerNumber == null) return null;
  return site.customerSubnumber && site.customerSubnumber > 0
    ? `${site.customerNumber}/${site.customerSubnumber}`
    : String(site.customerNumber);
}
